import React, { useEffect, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
  RowActions,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { FiEye, FiMapPin } from "react-icons/fi";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Branch } from "../../../../../../../models/branch";
import { DifferentLocation } from "../../../../../../../models/location";
import { Row } from "../../../../../../../models/shipeezi.table";
import { Warehouse } from "../../../../../../../models/warehouse";
import { Status } from "../../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import MapsModal, { MapsModalHandle } from "./components/Maps";
import useStyles, { BadgeStatus } from "./styles";
import useActions from "./useActions";
import WarehouseModal from "./WarehouseModal";
import { isShowAction } from "Util/permissions";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";

type Props = {
  permission?: {
    edit: boolean;
    create: boolean;
  };
};

const WarehousePage = ({ permission }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const {
    warehouses,
    page,
    pageSize,
    total,
    hasErrors,
    hasWarehouseCodeError,
    setHasErrors,
    setHasWarehouseCodeError,
    setPageSize,
    setCurrentPage,
    isLoading,
    handleSearch,
    handleFilter,
    handleSort,
    handleGetDefaultFilters,
    updateWarehouse,
    createWarehouse,
  } = useActions();
  const mapsModalRef = useRef<MapsModalHandle>(null);

  const [warehouse, setWarehouse] = useState<Warehouse>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isOnViewMode, setIsOnViewMode] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [clearSelection, setClearSelection] = useState<boolean>(false);
  const [warehousesInUse, setWarehousesInUse] = useState<Warehouse[]>([]);

  const handleEdit = (index: number) => {
    if (hasErrors !== undefined) setHasErrors(undefined);
    if (hasWarehouseCodeError !== undefined) setHasWarehouseCodeError(undefined);
    let warehouseList: Warehouse[] = [];

    try {
      warehouseList = JSON.parse(JSON.stringify(warehouses));
    } catch {
      warehouseList = [...warehouses];
    }
    const findWarehouse = warehouseList.find((warehouse) => warehouse.id === index);

    if (findWarehouse) {
      setWarehouse(findWarehouse);
      setIsOnViewMode(false);
      setOpenModal(true);
    }
  };

  const verifyIfAllWarehouseSectionsAreEmpty = (warehouse?: Warehouse) => {
    const emptySections = warehouse?.sections?.filter(
      (section) =>
        section.maps?.length === 0 || section.maps?.every((map) => map.isActive === false)
    );

    return emptySections?.length === warehouse?.sections?.length;
  };

  const handleChangeWarehouseStatus = (warehouseId: number) => {
    const findWarehouse = warehouses.find((warehouse) => warehouse.id === warehouseId);

    if (!verifyIfAllWarehouseSectionsAreEmpty(findWarehouse)) return null;

    if (findWarehouse) {
      return (
        <Tooltip title={translate("changeStatus")} placement="top">
          <div>
            <GridIcons.status
              className={classes.button}
              onClick={() => {
                updateWarehouse({
                  id: Number(warehouseId),
                  warehouse: {
                    ...findWarehouse,
                    status:
                      findWarehouse.status && findWarehouse.status === Status.ACTIVE
                        ? Status.INACTIVE
                        : Status.ACTIVE,
                  },
                });
              }}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const handleView = (index: number) => (
    <Tooltip title={translate("view")} placement="top">
      <div>
        <FiEye
          size={18}
          className={classes.button}
          onClick={() => {
            if (hasErrors !== undefined) setHasErrors(undefined);
            if (hasWarehouseCodeError !== undefined) setHasWarehouseCodeError(undefined);
            const findWarehouse = warehouses.find((warehouse) => warehouse.id === index);

            if (findWarehouse) {
              setWarehouse(findWarehouse);
              setIsOnViewMode(true);
              setOpenModal(true);
            }
          }}
        />
      </div>
    </Tooltip>
  );

  const handleMap = (index: number) => {
    const data = warehouses.find((warehouse) => warehouse.id === index);

    return (
      <Tooltip title={translate("map")} placement="top">
        <div>
          <FiMapPin
            className={classes.button}
            size={18}
            onClick={() => {
              if (data) {
                mapsModalRef.current?.setOpenModal(data);
              }
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const renderChangeAllStatus = () => {
    if (rows.length > 0 && rows.length !== warehousesInUse.length) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeAllStatusButton}
          data-cy="changeStatus"
          onClick={() => setOpenConfirmation(true)}
          customColor={Color.INFO}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveWarehouseSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, warehouses);

    const selectedWarehouses = [...rowsSelected] as Warehouse[];
    const selectedWarehousesInUse: Warehouse[] = selectedWarehouses.filter(
      (warehouse) => !verifyIfAllWarehouseSectionsAreEmpty(warehouse)
    );

    setWarehousesInUse(selectedWarehousesInUse);

    setRows([...rowsSelected]);
  };

  const columns: Column[] = [
    { name: "code", title: translate("code") },
    { name: "name", title: translate("name") },
    {
      name: "location",
      title: translate("address"),
      formatText: (location: DifferentLocation) => {
        if (location) {
          return location.country || location.suburb || location.state;
        }

        return "";
      },
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "branches",
      title: translate("branches"),
      formatText: (branches: Branch[]) => {
        if (branches) {
          const branchesName = branches.map((branch) => branch.name);

          return branchesName.join(" / ");
        }
      },
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "updatedAt",
      title: translate("updated"),
      formatText: (date: string) =>
        date && currentUser?.preferences
          ? formatDateWithUserPref(date, currentUser.preferences, "dateOnly")
          : "",
      hideSearch: true,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => (
        <BadgeStatus isActive={status.toLowerCase() === Status.ACTIVE.toLowerCase()}>
          {status.toLowerCase() === Status.ACTIVE.toLowerCase() ? Status.ACTIVE : Status.INACTIVE}
        </BadgeStatus>
      ),
      hideSearch: true,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "handleNewRecord"],
    },
    handleNewRecord:
      permission && isShowAction(currentUser, permission?.create)
        ? {
            text: translate("new"),
            action: () => {
              if (hasErrors !== undefined) setHasErrors(undefined);
              if (hasWarehouseCodeError !== undefined) setHasWarehouseCodeError(undefined);
              setWarehouse(undefined);
              setIsOnViewMode(false);
              setOpenModal(true);
            },
          }
        : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          options: [Status.ACTIVE, Status.INACTIVE],
          optionsSelected: handleGetDefaultFilters(),
        },
        {
          name: translate("name"),
          column: "name",
        },
        {
          name: translate("code"),
          column: "code",
        },
      ],
      onChange: handleFilter,
    },
    handleChangeStatus: renderChangeAllStatus(),
  };

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: translate("changeStatus"),
      key: "handleChangeWarehouseStatus",
      buttonAndAction: handleChangeWarehouseStatus,
    },
    { title: translate("view"), key: "handleView", buttonAndAction: handleView },
    { title: translate("map"), key: "map", buttonAndAction: handleMap },
  ];

  const rowActions: RowActions = {
    orderBy: ["handleEdit", "handleView", "handleChangeWarehouseStatus", "map"],
    handleEdit:
      permission && isShowAction(currentUser, permission?.edit)
        ? (warehouseId) => handleEdit(Number(warehouseId))
        : undefined,
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.notification.success.warehouse",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  const handleWarnWarehousesSectionsInUse = (warehousesSectionsInUse: Warehouse[]) => {
    const warehousesWithSectionsInUseNames = warehousesSectionsInUse
      .map((warehouse) => warehouse.name)
      .join(", ");

    return NotificationToast.warning(
      `${translate(
        "theFollowingWarehousesHaveSectionsWithMapsInUseAndCanNotChangeStatus"
      )} ${warehousesWithSectionsInUseNames}`,
      { id: "notification.warning.warehouse.sections.in.use" }
    );
  };

  const getEmptySectionsWarehouses = (selectedWarehouses: Warehouse[]) => {
    return selectedWarehouses.filter((map) => verifyIfAllWarehouseSectionsAreEmpty(map));
  };

  return (
    <div data-cy="warehouseList">
      <ShipeeziNewGrid
        data={warehouses}
        columns={columns}
        totalItems={total}
        loadingData={isLoading}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        clearSelection={clearSelection}
        handleSelect={(rows: number[]) => handleSaveWarehouseSelected(rows)}
        rowActions={rowActions}
        rowActionsExtra={rowActionsExtra}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />
      {openModal && (
        <WarehouseModal
          open={openModal}
          hasError={hasErrors}
          hasCodeError={hasWarehouseCodeError}
          setHasCodeError={setHasWarehouseCodeError}
          isOnViewMode={isOnViewMode}
          warehouseToUpdate={warehouse}
          isLoading={isLoading}
          closeModal={() => setOpenModal(false)}
          submit={(warehouse: Warehouse) => {
            if (warehouse.id) updateWarehouse({ id: warehouse.id, warehouse });
            else
              createWarehouse({
                ...warehouse,
                companyId: currentUser?.companyId,
                status: Status.ACTIVE,
              });
          }}
        />
      )}
      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={getEmptySectionsWarehouses(rows as Warehouse[])}
          isLoading={isLoading}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              const warehouse = rows.find((row) => row.id === rowId);

              updateWarehouse({
                id: rowId,
                warehouse: { ...warehouse, status },
              });

              if (warehousesInUse.length > 0) {
                handleWarnWarehousesSectionsInUse(warehousesInUse);
              }

              setClearSelection(true);
            }
          }}
        />
      )}

      <MapsModal ref={mapsModalRef} />
    </div>
  );
};

export default WarehousePage;
